import {
  LinkViewModel,
  LinksView,
  LinksViewProps,
  MapTitleFn,
  PageDataContext,
  isValidCuratedCollection,
} from '@/services/RenderService'
import { CollectionDisplay as CollectionDisplayType } from '@/types/codegen-contentful'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'
import {
  isContentCollection,
  mapContentCollectionIdsToCollections,
  mapContentCollectionsToViewModels,
} from '../ContentCollection'
import { isTitleCollection, mapContentIdsToTitles } from '../TitleCollection'
import { Banner } from './Banner'
import { LandscapeRail, mapToLink } from './LandscapeRail'
import { LargeLandscapeGrid } from './LargeLandscapeGrid'
import { LargePortraitGrid } from './LargePortraitGrid'
import { LargePortraitRail } from './LargePortraitRail'

interface CollectionDisplayProps {
  collectionDisplay: CollectionDisplayType
  pageDataContext: PageDataContext
  containerPosition?: number
}

/**
 * Combines a list of data and a display format in order to render a collection
 */
export function CollectionDisplay({
  collectionDisplay,
  pageDataContext,
  containerPosition = 0,
}: CollectionDisplayProps) {
  if (!collectionDisplay.displayAs) return null

  const Ui = mapUiFunction(collectionDisplay.displayAs)
  if (!Ui) {
    logger().error(
      `A "CollectionDisplay" was provided with an unsupported "displayAs" option; nothing will be rendered.`,
      { collectionDisplay },
    )
    return null
  }

  const links = mapCollectionLinkData(collectionDisplay, Ui.mapTitle, pageDataContext)

  return (
    <Ui
      id={collectionDisplay.sys.id}
      links={links}
      showTitleDescription={pageDataContext.contentItemConfig?.showThumbnailTitleDescription}
      containerPosition={containerPosition}
    />
  )
}

/**
 * Finds the appropriate data needed for the kind of collection and converts it to the shared view model
 */
function mapCollectionLinkData(
  collectionDisplay: CollectionDisplayType,
  mapTitleFn: MapTitleFn,
  pageDataContext: PageDataContext,
): LinkViewModel[] {
  if (isContentCollection(collectionDisplay.contentCollection)) {
    const contentCollections = mapContentCollectionIdsToCollections(
      pageDataContext,
      collectionDisplay.contentCollection?.contentCollectionId,
    )
    const linkViewModels = mapContentCollectionsToViewModels(contentCollections, collectionDisplay)
    const validLinkViewModels = linkViewModels.filter(isDefined)

    return validLinkViewModels
  }

  if (isTitleCollection(collectionDisplay.collection)) {
    const titles = mapContentIdsToTitles(pageDataContext, collectionDisplay.collection.contentCatalogIds)
    const linkViewModels = titles.map(mapTitleFn)
    const validLinkViewModels = linkViewModels.filter(isDefined)
    return validLinkViewModels
  }

  if (isValidCuratedCollection(collectionDisplay.curatedCollection)) {
    const dataSource = collectionDisplay.curatedCollection.dataSource
    if (!dataSource) {
      logger().error(
        `Failed to generate link view models for this curated collection because it is missing a data source.`,
        { curatedCollection: collectionDisplay.curatedCollection },
      )
      return []
    }
    const ids = pageDataContext[dataSource]
    const titles = mapContentIdsToTitles(pageDataContext, ids)
    const linkViewModels = titles.map(mapTitleFn)
    const validLinkViewModels = linkViewModels.filter(isDefined)
    return validLinkViewModels
  }

  logger().error(`Failed to generate link view models for this collectionDisplay.`, { collectionDisplay })
  return []
}

/**
 * Matches the displayAs to the appropriate UI.
 *
 * For now displayAs values, add the UI of type LinksView here.
 */
function mapUiFunction(displayAs: string | undefined): LinksView | undefined {
  return {
    'large-portrait-rail': LargePortraitRail,
    'large-portrait-grid': LargePortraitGrid,
    'large-landscape-grid': LargeLandscapeGrid,
    'landscape-rail': LandscapeRail,
    'landscape-small': LandscapeRail,
    landscape: undefined,
    'landscape-with-subtext': LandscapeRailWithSubtext,
    'landscape-small-with-subtext': undefined,
    'landscape-grid-with-subtext': LandscapeGridWithSubtext,
    banner: Banner,
  }[displayAs ?? '']
}

const LandscapeRailWithSubtext = (props: LinksViewProps) => {
  return <LandscapeRail {...props} showTitleDescription={true} />
}

const LandscapeGridWithSubtext = (props: LinksViewProps) => {
  return <LargeLandscapeGrid {...props} showTitleDescription={true} />
}

LandscapeGridWithSubtext.mapTitle = mapToLink
LandscapeRailWithSubtext.mapTitle = mapToLink
